import { Link } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SkillCategoryBadge } from "@/components/domain/skill-category-badge"
import type { Skill } from "@/types/skill"

type Agent = { id: number; name: string; key: string }

type Props = {
  skill: Skill
  agents: Agent[]
}

// Read-only skill detail. The Designer page is the default mutable
// surface; Manual edit is an alternative view.
export default function SkillsShow({ skill, agents }: Props) {
  return (
    <AppShell>
      <PageHeader
        eyebrow={<Link href="/skills" className="hover:text-app-text">Skills</Link>}
        title={skill.name}
        subtitle={skill.description ?? "—"}
        actions={
          <div className="flex items-center gap-2">
            <Link href={`/skills/${skill.id}/edit`}>
              <Button variant="ghost">Manual edit</Button>
            </Link>
            <Link href={`/skills/${skill.id}/test?tab=design`}>
              <Button variant="gold">Open Designer</Button>
            </Link>
          </div>
        }
      />

      <section className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="space-y-5 p-6">
          <div className="flex flex-wrap items-center gap-2">
            <SkillCategoryBadge category={skill.category} />
            <Badge tone={skill.live ? "gold" : skill.status === "draft" ? "red" : "neutral"}>
              {skill.versionLabel}
            </Badge>
            {skill.isGlobal && <Badge tone="neutral">Global</Badge>}
            <code className="rounded-chip bg-white/5 px-2 py-0.5 text-xs text-app-muted">{skill.key}</code>
          </div>

          {skill.description && (
            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-app-subtle">Description</div>
              <p className="whitespace-pre-wrap text-sm text-app-text">{skill.description}</p>
            </div>
          )}
        </Card>

        <aside className="space-y-4">
          <Card variant="inset" className="p-4">
            <div className="mb-2 text-xs uppercase tracking-wide text-app-subtle">
              Attached to {agents.length} agent{agents.length === 1 ? "" : "s"}
            </div>
            {agents.length === 0 ? (
              <p className="text-sm text-app-muted">Not attached to any agent.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {agents.map((a) => (
                  <li key={a.id}>
                    <Link href={`/agents/${a.id}/edit`} className="text-app-text hover:text-vip-goldSoft">
                      {a.name}
                    </Link>
                    <span className="ml-2 text-xs text-app-muted">{a.key}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card variant="inset" className="p-4">
            <div className="mb-1 text-xs uppercase tracking-wide text-app-subtle">Usage (30d)</div>
            <div className="text-2xl font-semibold">{skill.usageCount.toLocaleString()}</div>
            {skill.successRate != null && (
              <div className="mt-1 text-sm text-vip-goldSoft">
                {Math.round(skill.successRate * 100)}% success rate
              </div>
            )}
          </Card>

          <Card variant="inset" className="p-4">
            <div className="mb-1 text-xs uppercase tracking-wide text-app-subtle">Updated</div>
            <div className="text-sm text-app-muted">
              {new Date(skill.updatedAt).toLocaleString()}
            </div>
          </Card>
        </aside>
      </section>
    </AppShell>
  )
}
